import type {
  ILauncherService,
  LaunchHosting,
  LaunchItem,
} from '@hibro/shared';

/**
 * Optional sync open for the browser runtime (见 21 / 18).
 *
 * Must run inside the click handler: routing through the extension host drops
 * the user gesture and the popup blocker then fails the open. Returns whether
 * a window/tab was opened; `false` means the browser blocked it.
 */
export interface OpenExternalBridge {
  open(url: string): boolean;
}

export type OpenBrowserWindow = (url: string) => void;

export interface OpenAppLinkResult {
  readonly opened: boolean;
  /** True when `hosting` is not yet wired and we fell back to the browser. */
  readonly degraded: boolean;
}

/**
 * Resolve a launch item's `openUrl` action (见 21).
 *
 * `browser` opens the system/default browser. `inline` goes through
 * {@link OpenAppLinkOptions.openLink} so a shipped module can present the
 * address (见 20); `window` opens the built-in browser's independent window.
 * Missing callbacks and every other hosting degrade to the system browser.
 *
 * On web, `inline` is asynchronous (it crosses the extension host) and
 * therefore cannot keep a user gesture — the fallback uses the workbench
 * browser editor, which does not need one.
 */
export function openAppLink(
  item: Pick<LaunchItem, 'action' | 'title'>,
  options: {
    bridge?: OpenExternalBridge;
    launcherService: ILauncherService;
    /** Trusted renderer openLink, already bound to the launcher's moduleId. */
    openLink?: (url: string) => Promise<boolean>;
    /** User-activated built-in browser window open; must run in this click stack. */
    openWindow?: OpenBrowserWindow;
  },
): OpenAppLinkResult {
  const action = item.action;
  if (!action || action.type !== 'openUrl') {
    return { opened: false, degraded: false };
  }
  const hosting: LaunchHosting = action.hosting;
  if (hosting === 'inline' && options.openLink) {
    void options.openLink(action.url);
    return { opened: true, degraded: false };
  }
  if (hosting === 'window' && options.openWindow) {
    options.openWindow(action.url);
    return { opened: true, degraded: false };
  }
  const degraded = hosting !== 'browser';
  if (options.bridge) {
    return { opened: options.bridge.open(action.url), degraded };
  }
  void options.launcherService.openExternal(action.url);
  return { opened: true, degraded };
}
